import { useAuthentication } from "@aistihealth/web-common/src/msal/clientMsal"
import React, { FC, useContext, useEffect, useState } from "react"
import { createApolloContext } from "../api/gql/client"
import { GetMeQuery, useGetMeLazyQuery } from "../api/gql/generated/graphql"

const enableNewAdminAuthorizationModel =
  process.env.NEXT_PUBLIC_FEATURE_ENABLE_NEW_ADMIN_AUTHORIZATION_MODEL ===
  "true"

export interface AdminAuthzContextValue {
  adminUser?: GetMeQuery["me"]
  loading: boolean
}

// Used for old authz and non-admin pages
const defaultValue: AdminAuthzContextValue = {
  adminUser: { isSuperadmin: true },
  loading: false,
}

const AdminAuthzContext = React.createContext<AdminAuthzContextValue>(
  defaultValue,
)

/**
 * Provide the authz context.
 *
 * If the provider is not present, the default value pretends that the user is a superadmin to retain
 * compatibility with non-admin pages and old authz.
 */
export const AdminAuthzProvider: FC = ({ children }) => {
  const auth = useAuthentication()

  const [contextValue, setContextValue] = useState<AdminAuthzContextValue>({
    adminUser: undefined,
    loading: true,
  })

  const [fetchMe] = useGetMeLazyQuery()

  useEffect(() => {
    if (!enableNewAdminAuthorizationModel) {
      return
    }

    if (!auth) {
      setContextValue({
        adminUser: undefined,
        loading: true,
      })

      return
    }

    const execute = async () => {
      const result = await fetchMe({
        ...createApolloContext(auth),
      })

      setContextValue({
        adminUser: result.data.me,
        loading: false,
      })
    }

    execute()
  }, [auth, fetchMe])

  return (
    <AdminAuthzContext.Provider value={contextValue}>
      {children}
    </AdminAuthzContext.Provider>
  )
}

export const useAdminAuthz = (): AdminAuthzContextValue =>
  useContext(AdminAuthzContext)
